import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Truck, Gauge, FileText, Camera, History, ListOrdered, ScrollText, Wrench, Circle, Fuel, Upload, Trash2, Plus, Pencil, ChevronRight, Eye, ExternalLink, Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { PHOTO_CATEGORIES, SITUATION_TONES, LICENSING_TYPES } from '../utils/constants';
import { formatCurrency } from '../utils/format';
import DocumentViewerModal from '../components/DocumentViewerModal';
import { openFileInNewTab, downloadFile } from '../utils/fileViewer';

function SituationBadge({ value, toneOverride }) {
  const tone = toneOverride || SITUATION_TONES[value] || 'neutral';
  const map = {
    emerald: 'bg-emerald-100 text-emerald-700', blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-800', neutral: 'bg-neutral-200 text-neutral-700',
    purple: 'bg-purple-100 text-purple-700', red: 'bg-red-100 text-red-700',
  };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${map[tone]}`}>{value || '-'}</span>;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start gap-2 py-1.5 text-sm">
      <span className="text-neutral-500 dark:text-neutral-400 min-w-[160px]">{label}</span>
      <span className="font-medium">{value || '-'}</span>
    </div>
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ==================== TABS ====================

function ResumoTab({ v }) {
  const t = v.technical || {}; const o = v.operational || {}; const f = v.financial || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <h3 className="font-semibold mb-3">Identificação</h3>
        <InfoRow label="Placa" value={<span className="font-mono">{v.plate}</span>} />
        <InfoRow label="Nº da Frota" value={v.fleet_number} />
        <InfoRow label="Código interno" value={v.internal_code} />
        <InfoRow label="RENAVAM" value={v.renavam} />
        <InfoRow label="Chassi" value={<span className="font-mono text-xs">{v.chassis}</span>} />
        <InfoRow label="Marca / Modelo" value={`${v.brand} ${v.model}`} />
        <InfoRow label="Tipo" value={v.vehicle_type} />
        <InfoRow
          label="Empresa Proprietária"
          value={
            (v.owner_company_name || t.owner_company_name) ? (
              <span>
                <strong className="font-semibold text-neutral-800 dark:text-neutral-200">
                  {v.owner_company_name || t.owner_company_name}
                </strong>
                {(v.owner_company_cnpj || t.owner_company_cnpj) ? (
                  <span className="text-xs text-neutral-500 font-mono ml-2">
                    (CNPJ: {v.owner_company_cnpj || t.owner_company_cnpj})
                  </span>
                ) : null}
              </span>
            ) : '-'
          }
        />
        <InfoRow label="Cor" value={v.color} />
        <InfoRow label="Ano" value={`${v.year}${v.model_year ? ` / ${v.model_year}` : ''}`} />
      </div>
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
        <h3 className="font-semibold mb-3">Operacional</h3>
        <InfoRow label="Motorista responsável" value={o.driver_name} />
        <InfoRow label="Motorista reserva" value={o.reserve_driver_name} />
        <InfoRow label="Garagem" value={o.garage} />
        <InfoRow label="Localização" value={o.location} />
        <InfoRow label="Filial" value={v.branch} />
        <InfoRow label="KM atual" value={t.current_km ? Number(t.current_km).toLocaleString('pt-BR') : '-'} />
        <InfoRow label="Média de consumo" value={t.average_consumption ? `${t.average_consumption} km/l` : '-'} />
        {o.notes && <InfoRow label="Observações" value={<span className="text-xs">{o.notes}</span>} />}
      </div>
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 md:col-span-2">
        <h3 className="font-semibold mb-3">Financeiro</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8">
          <InfoRow label="Valor de aquisição" value={f.acquisition_value ? formatCurrency(f.acquisition_value) : '-'} />
          <InfoRow label="Data da compra" value={f.purchase_date ? new Date(f.purchase_date).toLocaleDateString('pt-BR') : '-'} />
          <InfoRow label="Fornecedor" value={f.supplier_name} />
          <InfoRow label="Vida útil" value={f.useful_life_years ? `${f.useful_life_years} anos` : '-'} />
          <InfoRow label="Valor contábil" value={f.book_value ? formatCurrency(f.book_value) : '-'} />
          <InfoRow label="Valor de mercado" value={f.market_value ? formatCurrency(f.market_value) : '-'} />
        </div>
      </div>
    </div>
  );
}

function TechnicalTab({ v, onSave }) {
  const [form, setForm] = useState({
    ...(v.technical || {}),
    owner_company_id: v.owner_company_id || v.technical?.owner_company_id || '',
  });
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/masters/owner-companies', { params: { limit: 100 } })
      .then(res => setCompanies(res.data?.items || res.data || []))
      .catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const selectedCompany = form.owner_company_id ? companies.find(c => c.id === form.owner_company_id) : null;
      const payload = {
        owner_company_id: form.owner_company_id || null,
        owner_company_name: selectedCompany ? selectedCompany.name : null,
        owner_company_cnpj: selectedCompany ? selectedCompany.cnpj : null,
        technical: { ...form,
          owner_company_id: form.owner_company_id || null,
          owner_company_name: selectedCompany ? selectedCompany.name : null,
          owner_company_cnpj: selectedCompany ? selectedCompany.cnpj : null,
        passenger_capacity: form.passenger_capacity ? Number(form.passenger_capacity) : null,
        tank_capacity: form.tank_capacity ? Number(form.tank_capacity) : null,
        gross_weight: form.gross_weight ? Number(form.gross_weight) : null,
        initial_km: Number(form.initial_km || 0),
        current_km: Number(form.current_km || 0),
        average_consumption: form.average_consumption ? Number(form.average_consumption) : null,
      }};
      await api.put(`/vehicles/${v.id}`, payload);
      toast.success('Dados técnicos salvos');
      onSave?.();
    } catch { toast.error('Erro ao salvar'); } finally { setSaving(false); }
  };
  const fields = [
    ['fuel_type', 'Combustível', 'text'],
    ['passenger_capacity', 'Capacidade passageiros', 'number'],
    ['tank_capacity', 'Capacidade do tanque (L)', 'number'],
    ['engine_number', 'Nº motor', 'text'],
    ['engine_power', 'Potência', 'text'],
    ['gearbox_type', 'Tipo de câmbio', 'text'],
    ['body_type', 'Tipo de carroceria', 'text'],
    ['gross_weight', 'Peso bruto (kg)', 'number'],
    ['initial_km', 'KM inicial', 'number'],
    ['current_km', 'KM atual', 'number'],
    ['average_consumption', 'Consumo médio (km/l)', 'number'],
  ];
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 space-y-5">
      <div className="bg-neutral-50 dark:bg-neutral-800/50 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center justify-between mb-1.5">
          <Label className="font-semibold text-neutral-800 dark:text-neutral-200">Empresa Proprietária (CNPJ)</Label>
          <a href="/cadastros/empresas-proprietarias" target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline font-medium">
            + Gerenciar Empresas / CNPJs
          </a>
        </div>
        <select
          value={form.owner_company_id || ''}
          onChange={e => setForm({ ...form, owner_company_id: e.target.value })}
          className="w-full h-10 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand dark:border-neutral-800 dark:bg-neutral-950 font-medium"
        >
          <option value="">Nenhuma / Sem vínculo</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>
              {c.name} {c.cnpj ? `— CNPJ: ${c.cnpj}` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-neutral-500 mt-1">Vincula o veículo à empresa proprietária responsável.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {fields.map(([k, label, type]) => (
          <div key={k} className="space-y-1.5">
            <Label>{label}</Label>
            <Input type={type} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end">
        <Button data-testid="save-technical-btn" onClick={save} disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}

function MileageTab({ v, onChange }) {
  const [items, setItems] = useState([]);
  const [chart, setChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [km, setKm] = useState('');
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get(`/vehicles/${v.id}/mileage`, { params: { limit: 100 } }),
        api.get(`/vehicles/${v.id}/mileage/chart`),
      ]);
      setItems(list.items || []);
      setChart(c || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [v.id]);

  const submit = async () => {
    try {
      await api.post(`/vehicles/${v.id}/mileage`, { km: Number(km), notes });
      toast.success('KM registrado');
      setOpen(false); setKm(''); setNotes('');
      await load();
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Histórico de Quilometragem</h3>
          <p className="text-xs text-neutral-500">KM atual: {(v.technical?.current_km || 0).toLocaleString('pt-BR')}</p>
        </div>
        <Button data-testid="add-mileage-btn" onClick={() => setOpen(true)} className="bg-brand hover:bg-[#A81E23] text-white">
          <Plus className="h-4 w-4 mr-2" /> Nova leitura
        </Button>
      </div>

      {chart.length > 1 && (
        <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
          <p className="text-xs text-neutral-500 mb-2">Evolução da quilometragem</p>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#999" />
                <YAxis tick={{ fontSize: 11 }} stroke="#999" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `${Number(v).toLocaleString('pt-BR')} km`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="km" stroke="#C52429" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Data</th>
              <th className="text-left px-4 py-3">KM</th>
              <th className="text-left px-4 py-3">Usuário</th>
              <th className="text-left px-4 py-3">Observações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={4} className="p-4"><TableSkeleton rows={4} cols={4} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={4}><EmptyState icon={Gauge} title="Nenhuma leitura registrada" /></td></tr> :
              items.map(m => (
                <tr key={m.id} className="tr-hover">
                  <td className="px-4 py-3">{new Date(m.date).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3 font-medium">{Number(m.km).toLocaleString('pt-BR')} km</td>
                  <td className="px-4 py-3 text-xs">{m.user_name}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{m.notes || '-'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova leitura de KM</DialogTitle>
            <DialogDescription>Não é possível reduzir a quilometragem atual.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Quilometragem *</Label>
              <Input data-testid="mileage-input" type="number" value={km} onChange={e => setKm(e.target.value)} placeholder={`Atual: ${v.technical?.current_km || 0}`} />
            </div>
            <div className="space-y-1.5">
              <Label>Observações</Label>
              <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="save-mileage-btn" onClick={submit} className="bg-brand hover:bg-[#A81E23] text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotosTab({ v }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('frente');
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/vehicles/${v.id}/photos`); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [v.id]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error('Imagem muito grande (máx 3MB)'); return; }
    try {
      const url = await fileToDataUrl(file);
      await api.post(`/vehicles/${v.id}/photos`, {
        category, name: file.name, url, mime_type: file.type, size: file.size,
      });
      toast.success('Foto enviada');
      load();
    } catch { toast.error('Erro no upload'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/vehicles/${v.id}/photos/${deleteId}`);
      toast.success('Foto excluída'); setDeleteId(null); load();
    } catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>Categoria:</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="photo-category" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>{PHOTO_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()} className="bg-brand hover:bg-[#A81E23] text-white">
          <Upload className="h-4 w-4 mr-2" /> Enviar foto
        </Button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>

      {loading ? <TableSkeleton rows={2} cols={4} /> :
        items.length === 0 ? <EmptyState icon={Camera} title="Nenhuma foto ainda" description="Envie fotos categorizadas do veículo." /> :
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(p => (
            <div key={p.id} className="group relative rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
              <img src={p.url} alt={p.name} className="w-full h-40 object-cover" />
              <div className="p-2 flex items-center justify-between">
                <span className="text-[10px] uppercase text-neutral-500">{PHOTO_CATEGORIES.find(c => c.key === p.category)?.label || p.category}</span>
                <Button size="icon" variant="ghost" className="text-red-600 h-7 w-7" onClick={() => setDeleteId(p.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      }

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir foto?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DocumentsTab({ v }) {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ document_type_id: '', document_type_name: '', number: '', emission_date: '', expiration_date: '', cost: '', vencimentos: [], notes: '', file_name: '', file_url: '', mime_type: '', size: 0 });

  const addVencimento = () => {
    setForm(f => ({
      ...f,
      vencimentos: [
        ...(f.vencimentos || []),
        { description: `Parcela ${(f.vencimentos?.length || 0) + 1}`, expiration_date: '', value: '' }
      ]
    }));
  };

  const updateVencimento = (index, field, value) => {
    setForm(f => {
      const list = [...(f.vencimentos || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, vencimentos: list };
    });
  };

  const removeVencimento = (index) => {
    setForm(f => {
      const list = (f.vencimentos || []).filter((_, i) => i !== index);
      return { ...f, vencimentos: list };
    });
  };

  const calculatedTotalCost = (form.vencimentos || []).reduce((acc, it) => acc + (parseFloat(it.value) || 0), 0);
  const [deleteId, setDeleteId] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState({ url: '', name: '', title: '' });
  const fileRef = useRef(null);

  const handleViewDoc = (doc) => {
    setViewerFile({
      url: doc.file_url,
      name: doc.file_name || `${doc.document_type_name || 'documento'}.pdf`,
      title: `${doc.document_type_name || 'Documento'} - ${doc.number || ''}`.trim()
    });
    setViewerOpen(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: t }] = await Promise.all([
        api.get(`/vehicles/${v.id}/documents`, { params: { limit: 100 } }),
        api.get(`/masters/document-types`, { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(data.items || []);
      setTypes(t.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [v.id]);

  const openCreate = () => {
    setEditing(null);
    setForm({ document_type_id: '', document_type_name: '', number: '', emission_date: '', expiration_date: '', cost: '', vencimentos: [], notes: '', file_name: '', file_url: '', mime_type: '', size: 0 });
    setOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      document_type_id: d.document_type_id || '',
      document_type_name: d.document_type_name || '',
      number: d.number || '',
      emission_date: d.emission_date ? d.emission_date.slice(0, 10) : '',
      expiration_date: d.expiration_date ? d.expiration_date.slice(0, 10) : '',
      cost: d.cost ?? '',
      vencimentos: d.vencimentos || [],
      notes: d.notes || '',
      file_name: d.file_name || '', file_url: d.file_url || '',
      mime_type: d.mime_type || '', size: d.size || 0,
    });
    setOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande (máx 5MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm(f => ({ ...f, file_name: file.name, file_url: url, mime_type: file.type, size: file.size }));
    toast.success('Arquivo carregado (clique em salvar)');
  };

  const submit = async () => {
    try {
      const payload = { ...form };
      if (form.document_type_id) {
        const found = types.find(t => t.id === form.document_type_id);
        if (found) payload.document_type_name = found.name;
      }
      payload.emission_date = form.emission_date || null;
      payload.vencimentos = form.vencimentos || [];

      const hasVencs = payload.vencimentos.length > 0;
      if (hasVencs) {
        payload.cost = calculatedTotalCost;
        const validDates = payload.vencimentos.map(x => x.expiration_date).filter(Boolean).sort();
        payload.expiration_date = validDates[0] || form.expiration_date || null;
      } else {
        payload.cost = form.cost ? parseFloat(form.cost) : null;
        payload.expiration_date = form.expiration_date || null;
      }

      if (form.bind_to === 'company' && (v.owner_company_id || v.technical?.owner_company_id)) {
        payload.owner_company_id = v.owner_company_id || v.technical?.owner_company_id;
        payload.owner_company_id = v.owner_company_id;
      } else {
        payload.owner_company_id = null;
      }
      if (editing) await api.put(`/vehicles/${v.id}/documents/${editing.id}`, payload);
      else await api.post(`/vehicles/${v.id}/documents`, payload);
      toast.success(editing ? 'Documento atualizado' : 'Documento adicionado');
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/vehicles/${v.id}/documents/${deleteId}`); toast.success('Documento excluído'); setDeleteId(null); load(); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Documentos</h3>
          <p className="text-xs text-neutral-500">Gerenciar documentos do veículo (CNH, CRLV, seguro, contratos, etc.)</p>
        </div>
        <Button data-testid="add-document-btn" onClick={openCreate} className="bg-brand hover:bg-[#A81E23] text-white">
          <Plus className="h-4 w-4 mr-2" /> Adicionar
        </Button>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Número</th>
              <th className="text-left px-4 py-3">Emissão</th>
              <th className="text-left px-4 py-3">Vencimento</th>
              <th className="text-left px-4 py-3">Arquivo</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={6} className="p-4"><TableSkeleton rows={3} cols={6} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={6}><EmptyState icon={FileText} title="Nenhum documento" description="Adicione documentos do veículo." /></td></tr> :
              items.map(d => (
                <tr key={d.id} className="tr-hover">
                  <td className="px-4 py-3">{d.document_type_name || '-'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{d.number || '-'}</td>
                  <td className="px-4 py-3 text-xs">{d.emission_date ? new Date(d.emission_date).toLocaleDateString('pt-BR') : '-'}</td>
                  <td className="px-4 py-3 text-xs">
                    {d.expiration_date ? new Date(d.expiration_date).toLocaleDateString('pt-BR') : '-'}
                    {d.vencimentos?.length > 1 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 font-semibold">
                        {d.vencimentos.length} parcelas
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-medium">
                    {d.cost ? formatCurrency(d.cost) : (d.vencimentos?.length ? formatCurrency(d.vencimentos.reduce((s, v) => s + (parseFloat(v.value) || 0), 0)) : '-')}
                  </td>
                  <td className="px-4 py-3">
                    {d.file_url ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleViewDoc(d)}
                          className="text-brand text-xs underline font-medium hover:text-[#A81E23] flex items-center gap-1 max-w-[220px] truncate text-left"
                          title="Clique para visualizar o documento"
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0 text-brand" />
                          <span className="truncate">{d.file_name || 'Visualizar PDF'}</span>
                        </button>

                        <div className="inline-flex items-center gap-0.5">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-neutral-500 hover:text-brand"
                            onClick={() => handleViewDoc(d)}
                            title="Visualizar documento"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
                            onClick={() => openFileInNewTab(d.file_url, d.file_name || 'documento.pdf')}
                            title="Abrir em nova guia"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-neutral-500 hover:text-emerald-600"
                            onClick={() => downloadFile(d.file_url, d.file_name || 'documento.pdf')}
                            title="Baixar arquivo"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(d)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(d.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Novo'} documento</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Tipo de documento</Label>
              <Select value={form.document_type_id || 'none'} onValueChange={v2 => setForm(f => ({ ...f, document_type_id: v2 === 'none' ? '' : v2 }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Número</Label>
              <Input value={form.number} onChange={e => setForm(f => ({ ...f, number: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Emissão</Label>
              <Input type="date" value={form.emission_date} onChange={e => setForm(f => ({ ...f, emission_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Vencimento</Label>
              <Input type="date" value={form.expiration_date} onChange={e => setForm(f => ({ ...f, expiration_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Arquivo</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" /> Escolher arquivo
                </Button>
                <span className="text-xs text-neutral-500 truncate">{form.file_name || 'Nenhum arquivo'}</span>
              </div>
              <input ref={fileRef} type="file" onChange={handleFile} className="hidden" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="save-document-btn" onClick={submit} className="bg-brand hover:bg-[#A81E23] text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir documento?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentViewerModal
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        fileUrl={viewerFile.url}
        fileName={viewerFile.name}
        title={viewerFile.title}
      />
    </div>
  );
}

function HistoryTab({ v }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/history', { params: { entity_type: 'vehicle', entity_id: v.id, limit: 100 } });
        setItems(data.items || []);
      } finally { setLoading(false); }
    })();
  }, [v.id]);
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
          <tr>
            <th className="text-left px-4 py-3">Data</th>
            <th className="text-left px-4 py-3">Ação</th>
            <th className="text-left px-4 py-3">Usuário</th>
            <th className="text-left px-4 py-3">Alterações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {loading ? <tr><td colSpan={4} className="p-4"><TableSkeleton rows={3} cols={4} /></td></tr> :
            items.length === 0 ? <tr><td colSpan={4}><EmptyState icon={History} title="Nenhum registro histórico" /></td></tr> :
            items.map(h => (
              <tr key={h.id} className="tr-hover">
                <td className="px-4 py-3 text-xs">{new Date(h.changed_at).toLocaleString('pt-BR')}</td>
                <td className="px-4 py-3"><span className="text-xs font-medium">{h.action}</span></td>
                <td className="px-4 py-3 text-xs">{h.changed_by_name}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">
                  {(h.changes || []).slice(0, 3).map((c, i) => (
                    <div key={i}><span className="font-mono">{c.field}</span>: {String(c.old_value ?? '-')} → {String(c.new_value ?? '-')}</div>
                  ))}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function TimelineTab({ v }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/vehicles/${v.id}/timeline`, { params: { limit: 100 } }); setItems(data.items || []); }
      finally { setLoading(false); }
    })();
  }, [v.id]);
  const eventDot = {
    created: 'bg-emerald-500', updated: 'bg-blue-500', deleted: 'bg-red-500',
    status_change: 'bg-amber-500', mileage_update: 'bg-purple-500',
    photo_added: 'bg-pink-500', photo_removed: 'bg-neutral-400',
    document_added: 'bg-indigo-500', document_updated: 'bg-indigo-400', document_removed: 'bg-neutral-400',
  };
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      {loading ? <TableSkeleton rows={4} cols={2} /> :
        items.length === 0 ? <EmptyState icon={ListOrdered} title="Timeline vazia" /> :
        <ol className="relative border-l-2 border-neutral-200 dark:border-neutral-800 ml-2 space-y-4">
          {items.map(e => (
            <li key={e.id} className="pl-6 relative">
              <span className={`absolute -left-[9px] top-1.5 h-4 w-4 rounded-full border-2 border-white dark:border-neutral-900 ${eventDot[e.event_type] || 'bg-neutral-400'}`} />
              <div className="text-sm font-medium">{e.description}</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {new Date(e.created_at).toLocaleString('pt-BR')} • {e.user_name}
              </div>
            </li>
          ))}
        </ol>
      }
    </div>
  );
}

function ComingSoonTab({ title, description, icon: Icon }) {
  return (
    <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-8">
      <div className="flex flex-col items-center text-center max-w-md mx-auto">
        <div className="h-16 w-16 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-4">
          <Icon className="h-8 w-8 text-neutral-400" />
        </div>
        <h3 className="font-semibold text-lg">{title}</h3>
        <p className="text-sm text-neutral-500 mt-1">{description}</p>
        <span className="mt-4 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Fase 4 — em breve</span>
      </div>
    </div>
  );
}

function LicensingTab({ v }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState({ url: '', name: '', title: '' });

  const handleViewLic = (lic) => {
    setViewerFile({
      url: lic.attachment_url,
      name: lic.file_name || `Licenciamento_${lic.type || 'CRLV'}.pdf`,
      title: `Licenciamento ${lic.type || ''}`.trim()
    });
    setViewerOpen(true);
  };

  const [form, setForm] = useState({
    type: '',
    issue_date: '',
    expiration_date: '',
    cost: '',
    notes: '',
    file_name: '',
    attachment_url: '',
    bind_to: 'vehicle',
    contract_id: '',
    metroplan_categories: [],
    vencimentos: [],
  });
  const fileRef = useRef(null);

  const addVencimento = () => {
    setForm(f => ({
      ...f,
      vencimentos: [
        ...(f.vencimentos || []),
        { description: `Parcela ${(f.vencimentos?.length || 0) + 1}`, expiration_date: '', value: '' }
      ]
    }));
  };

  const updateVencimento = (index, field, value) => {
    setForm(f => {
      const list = [...(f.vencimentos || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, vencimentos: list };
    });
  };

  const removeVencimento = (index) => {
    setForm(f => {
      const list = (f.vencimentos || []).filter((_, i) => i !== index);
      return { ...f, vencimentos: list };
    });
  };

  const calculatedTotalCost = (form.vencimentos || []).reduce((acc, it) => acc + (parseFloat(it.value) || 0), 0);

  const load = async () => {
    try {
      const { data } = await api.get('/licenses', { params: { vehicle_id: v.id, limit: 100 } });
      setItems(data.items || []);
    } catch { toast.error('Erro ao carregar licenciamentos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [v.id]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      type: '',
      issue_date: '',
      expiration_date: '',
      cost: '',
      notes: '',
      file_name: '',
      attachment_url: '',
      bind_to: 'vehicle',
      contract_id: '',
      metroplan_categories: [],
      vencimentos: [],
    });
    setOpen(true);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({
      type: l.type || '',
      issue_date: l.issue_date ? l.issue_date.slice(0, 10) : '',
      expiration_date: l.expiration_date ? l.expiration_date.slice(0, 10) : '',
      cost: l.cost || '',
      notes: l.notes || '',
      file_name: l.attachment_url ? 'Anexo' : '',
      attachment_url: l.attachment_url || '',
      bind_to: l.owner_company_id ? 'company' : 'vehicle',
      contract_id: l.contract_id || '',
      metroplan_categories: l.metroplan_categories || [],
      vencimentos: l.vencimentos || [],
    });
    setOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande (máx 5MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm(f => ({ ...f, file_name: file.name, attachment_url: url }));
    toast.success('Arquivo carregado (clique em salvar)');
  };

  const submit = async () => {
    if (!form.type) {
      toast.error('O Tipo de licenciamento é obrigatório');
      return;
    }
    const hasVencs = form.vencimentos && form.vencimentos.length > 0;
    if (!hasVencs && !form.expiration_date) {
      toast.error('Informe a data de vencimento ou adicione ao menos um vencimento');
      return;
    }
    try {
      const payload = { ...form, vehicle_id: v.id };
      payload.issue_date = form.issue_date || null;
      payload.contract_id = form.contract_id || null;
      payload.metroplan_categories = form.metroplan_categories || [];
      payload.vencimentos = form.vencimentos || [];

      if (hasVencs) {
        payload.cost = calculatedTotalCost;
        const validDates = payload.vencimentos.map(x => x.expiration_date).filter(Boolean).sort();
        payload.expiration_date = validDates[0] || form.expiration_date || null;
      } else {
        payload.cost = form.cost ? parseFloat(form.cost) : null;
        payload.expiration_date = form.expiration_date || null;
      }

      if (form.bind_to === 'company' && (v.owner_company_id || v.technical?.owner_company_id)) {
        payload.owner_company_id = v.owner_company_id || v.technical?.owner_company_id;
        payload.vehicle_id = null;
      } else {
        payload.vehicle_id = v.id;
        payload.owner_company_id = null;
      }
      if (editing) await api.put(`/licenses/${editing.id}`, payload);
      else await api.post('/licenses', payload);
      toast.success(editing ? 'Licenciamento atualizado' : 'Licenciamento adicionado');
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/licenses/${deleteId}`); toast.success('Licenciamento excluído'); setDeleteId(null); load(); }
    catch { toast.error('Erro ao excluir'); }
  };

  const getStatus = (expiration_date) => {
    if (!expiration_date) return null;
    const exp = new Date(expiration_date + 'T23:59:59');
    const now = new Date();
    const diffDays = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: 'Vencido', tone: 'red' };
    if (diffDays <= 90) return { label: `Vence em ${diffDays} dias`, tone: 'amber' };
    return { label: 'Regular', tone: 'emerald' };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Licenciamentos</h3>
          <p className="text-xs text-neutral-500">Controle de CRLV, IPVA, seguros, ANTT e vistorias</p>
        </div>
        <Button onClick={openCreate} className="bg-brand hover:bg-[#A81E23] text-white">
          <Plus className="h-4 w-4 mr-2" /> Adicionar
        </Button>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Tipo</th>
              <th className="text-left px-4 py-3">Emissão</th>
              <th className="text-left px-4 py-3">Vencimento</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Custo</th>
              <th className="text-left px-4 py-3">Anexo</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={7} className="p-4"><TableSkeleton rows={3} cols={7} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={7}><EmptyState icon={ScrollText} title="Nenhum licenciamento" description="Adicione os licenciamentos do veículo." /></td></tr> :
              items.map(l => {
                const status = getStatus(l.expiration_date);
                return (
                <tr key={l.id} className="tr-hover">
                  <td className="px-4 py-3">{l.type}</td>
                  <td className="px-4 py-3 text-xs">{l.issue_date ? new Date(l.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</td>
                  <td className="px-4 py-3 text-xs font-medium">
                    {l.expiration_date ? new Date(l.expiration_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                    {l.vencimentos?.length > 1 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 font-semibold">
                        {l.vencimentos.length} parcelas
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">{status && <SituationBadge value={status.label} toneOverride={status.tone} />}</td>
                  <td className="px-4 py-3 text-xs">{l.cost ? formatCurrency(l.cost) : '-'}</td>
                  <td className="px-4 py-3">
                    {l.attachment_url ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleViewLic(l)}
                          className="text-brand text-xs underline font-medium hover:text-[#A81E23] flex items-center gap-1 max-w-[200px] truncate text-left"
                          title="Clique para visualizar o anexo"
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0 text-brand" />
                          <span className="truncate">{l.file_name || 'Visualizar Anexo'}</span>
                        </button>

                        <div className="inline-flex items-center gap-0.5">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-neutral-500 hover:text-brand"
                            onClick={() => handleViewLic(l)}
                            title="Visualizar anexo"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
                            onClick={() => openFileInNewTab(l.attachment_url, l.file_name || 'licenciamento.pdf')}
                            title="Abrir em nova guia"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-neutral-500 hover:text-emerald-600"
                            onClick={() => downloadFile(l.attachment_url, l.file_name || 'licenciamento.pdf')}
                            title="Baixar arquivo"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(l.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              )})}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Novo'} licenciamento</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Tipo</Label>
              <Select value={form.type || 'none'} onValueChange={v => setForm(f => ({ ...f, type: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {LICENSING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data de Emissão</Label>
              <Input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} />
            </div>

            {/* Seção Vencimentos e Valores */}
            <div className="space-y-2 md:col-span-2 border-t pt-4 mt-1">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">Vencimentos e Valores</h4>
                  <p className="text-xs text-neutral-500">Adicione múltiplos vencimentos e parcelas caso aplicável.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addVencimento} className="h-8">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar Linha
                </Button>
              </div>

              {(!form.vencimentos || form.vencimentos.length === 0) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 bg-neutral-50 dark:bg-neutral-900/50 p-3 rounded-lg border border-dashed border-neutral-200 dark:border-neutral-800">
                  <div className="space-y-1">
                    <Label className="text-xs">Data de Vencimento Única *</Label>
                    <Input type="date" value={form.expiration_date || ''} onChange={e => setForm(f => ({ ...f, expiration_date: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valor Total (R$)</Label>
                    <Input type="number" step="0.01" value={form.cost || ''} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="0,00" />
                  </div>
                </div>
              ) : (
                <div className="space-y-2 mt-2">
                  {form.vencimentos.map((venc, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row gap-2 sm:items-end bg-neutral-50 dark:bg-neutral-900 p-2.5 rounded-lg border border-neutral-100 dark:border-neutral-800">
                      <div className="flex-1 space-y-1">
                        <Label className="text-[10px] uppercase font-semibold text-neutral-500">Descrição / Parcela</Label>
                        <Input className="h-8 text-sm" value={venc.description || ''} onChange={e => updateVencimento(idx, 'description', e.target.value)} placeholder={`Ex: Parcela ${idx + 1}`} />
                      </div>
                      <div className="w-full sm:w-40 space-y-1">
                        <Label className="text-[10px] uppercase font-semibold text-neutral-500">Vencimento *</Label>
                        <Input className="h-8 text-sm" type="date" value={venc.expiration_date || ''} onChange={e => updateVencimento(idx, 'expiration_date', e.target.value)} />
                      </div>
                      <div className="w-full sm:w-32 space-y-1">
                        <Label className="text-[10px] uppercase font-semibold text-neutral-500">Valor (R$)</Label>
                        <Input className="h-8 text-sm" type="number" step="0.01" value={venc.value || ''} onChange={e => updateVencimento(idx, 'value', e.target.value)} placeholder="0,00" />
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 shrink-0" onClick={() => removeVencimento(idx)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  <div className="pt-2 flex justify-end items-center">
                    <div className="bg-brand/10 text-brand px-3 py-1.5 rounded-lg border border-brand/20 flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase">Total Calculado:</span>
                      <span className="font-bold text-sm">{formatCurrency(calculatedTotalCost)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Anexo</Label>
              <div className="flex items-center gap-2 mt-0.5">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="h-9">
                  <Upload className="h-4 w-4 mr-2" /> {form.attachment_url ? 'Trocar arquivo' : 'Escolher'}
                </Button>
              </div>
              <input ref={fileRef} type="file" onChange={handleFile} className="hidden" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-brand hover:bg-[#A81E23] text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir licenciamento?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentViewerModal
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        fileUrl={viewerFile.url}
        fileName={viewerFile.name}
        title={viewerFile.title}
      />
    </div>
  );
}

// ==================== MAIN ====================

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'resumo';
  
  const handleTabChange = (val) => {
    setSearchParams(prev => { prev.set('tab', val); return prev; }, { replace: true });
  };
  
  const handleBack = () => {
    if (location.state && location.state.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };
  const [v, setV] = useState(null);
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/masters/owner-companies', { params: { limit: 100 } })
      .then(r => setOwnerCompanies(r.data.items || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    try { const { data } = await api.get(`/vehicles/${id}`); setV(data); }
    catch { toast.error('Veículo não encontrado'); navigate(-1); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !v) {
    return (
      <div>
        <PageHeader title="Veículo" subtitle="Carregando..." />
        <div className="h-96 shimmer rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <button data-testid="back-to-fleet" onClick={handleBack} className="flex items-center gap-1 text-xs text-neutral-500 hover:text-brand mb-2">
            <ArrowLeft className="h-3 w-3" /> Voltar para Frota
          </button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-3">
                <span className="font-mono">{v.plate}</span>
                <SituationBadge value={v.situation || v.status} />
              </h1>
              <p className="text-sm text-neutral-500 flex items-center gap-2 mt-0.5">
                {v.brand} {v.model} • {v.year}{v.model_year ? `/${v.model_year}` : ''}
                {v.fleet_number && <><ChevronRight className="h-3 w-3" /> Frota {v.fleet_number}</>}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="flex flex-wrap h-auto justify-start bg-neutral-100 dark:bg-neutral-900 p-1 mb-4">
          <TabsTrigger data-testid="tab-resumo" value="resumo"><Truck className="h-3.5 w-3.5 mr-1.5" />Resumo</TabsTrigger>
          <TabsTrigger data-testid="tab-tecnico" value="tecnico"><Gauge className="h-3.5 w-3.5 mr-1.5" />Técnicos</TabsTrigger>
          <TabsTrigger data-testid="tab-documentos" value="documentos"><FileText className="h-3.5 w-3.5 mr-1.5" />Documentos</TabsTrigger>
          <TabsTrigger data-testid="tab-quilometragem" value="quilometragem"><Gauge className="h-3.5 w-3.5 mr-1.5" />Quilometragem</TabsTrigger>
          <TabsTrigger data-testid="tab-fotos" value="fotos"><Camera className="h-3.5 w-3.5 mr-1.5" />Fotos</TabsTrigger>
          <TabsTrigger data-testid="tab-historico" value="historico"><History className="h-3.5 w-3.5 mr-1.5" />Histórico</TabsTrigger>
          <TabsTrigger data-testid="tab-timeline" value="timeline"><ListOrdered className="h-3.5 w-3.5 mr-1.5" />Timeline</TabsTrigger>
          <TabsTrigger data-testid="tab-licenciamentos" value="licenciamentos"><ScrollText className="h-3.5 w-3.5 mr-1.5" />Licenciamentos</TabsTrigger>
          <TabsTrigger data-testid="tab-manutencoes" value="manutencoes"><Wrench className="h-3.5 w-3.5 mr-1.5" />Manutenções</TabsTrigger>
          <TabsTrigger data-testid="tab-pneus" value="pneus"><Circle className="h-3.5 w-3.5 mr-1.5" />Pneus</TabsTrigger>
          <TabsTrigger data-testid="tab-abastecimentos" value="abastecimentos"><Fuel className="h-3.5 w-3.5 mr-1.5" />Abastecimentos</TabsTrigger>
        </TabsList>

        <TabsContent value="resumo"><ResumoTab v={v} /></TabsContent>
        <TabsContent value="tecnico"><TechnicalTab v={v} onSave={load} /></TabsContent>
        <TabsContent value="documentos"><DocumentsTab v={v} /></TabsContent>
        <TabsContent value="quilometragem"><MileageTab v={v} onChange={load} /></TabsContent>
        <TabsContent value="fotos"><PhotosTab v={v} /></TabsContent>
        <TabsContent value="historico"><HistoryTab v={v} /></TabsContent>
        <TabsContent value="timeline"><TimelineTab v={v} /></TabsContent>
        <TabsContent value="licenciamentos"><LicensingTab v={v} /></TabsContent>
        <TabsContent value="manutencoes"><ComingSoonTab title="Manutenções" icon={Wrench} description="Ordens de serviço, agendamentos preventivos e corretivos, histórico de intervenções." /></TabsContent>
        <TabsContent value="pneus"><ComingSoonTab title="Controle de Pneus" icon={Circle} description="Controle individual de pneus, rodízios, DOT, sulco e vida útil." /></TabsContent>
        <TabsContent value="abastecimentos"><ComingSoonTab title="Abastecimentos" icon={Fuel} description="Registro de abastecimentos, cálculo de consumo médio e custo por km." /></TabsContent>
      </Tabs>
    </div>
  );
}
